/**
 * Mock chat messages with behaviour slots for testing.
 *
 * Usage:
 * 1. Set ENABLE_MOCK_BEHAVIOUR to true in this file
 * 2. Add VITE_MOCK_EXECUTION_ID=<your-execution-id> to your .env.local
 * 3. The Chat component will inject mock messages ONLY for that specific execution
 * 4. All other chats work normally
 *
 * Remember to set ENABLE_MOCK_BEHAVIOUR back to false before committing.
 */

/** Toggle this to inject mock behaviour messages into a specific chat execution */
export const ENABLE_MOCK_BEHAVIOUR = false;

/**
 * Target execution ID for mock behaviour.
 * When ENABLE_MOCK_BEHAVIOUR is true, mock messages will ONLY be injected
 * for the chat with this execution ID. All other chats work normally.
 * Set via VITE_MOCK_EXECUTION_ID environment variable.
 */
export const MOCK_EXECUTION_ID = import.meta.env.VITE_MOCK_EXECUTION_ID || '';

/**
 * Which mock scenario to show when ENABLE_MOCK_BEHAVIOUR is true.
 * Switch between these to test different slot experiences:
 *  - 'insight_preview'  — insight cards + report preview slots
 *  - 'content_editor'  — editable rich-text content slot
 *  - 'keyword_review'  — interactive keyword review with approve/reject (mardi agent)
 */
export type MockScenario = 'insight_preview' | 'content_editor' | 'keyword_review';
export const ACTIVE_MOCK_SCENARIO = 'keyword_review' as MockScenario;

import type { ChatEntry } from '@/contexts/ChatContext';
import type { UIBlock } from '@/types/ui-blocks';
import type { ApprovalItem } from '@/components/keyword-review/types';

const blocksWithBehaviour: UIBlock[] = [
  {
    type: 'text',
    version: 'v1',
    content:
      '## Campaign Analysis Complete\n\nHere is a summary of your Google Ads campaign performance for Q4. I found two key insights worth your attention.',
    behaviours: [
      {
        id: 'b2a30ef7-ee57-4e5a-a689-38032f4ae141',
        trigger: 'auto',
        action: {
          id: 'b0758457-d9be-466b-88a5-c4fc7307ef0e',
          type: 'agent_experience',
          agent_short_name: 'insight',
          experience: 'insight_preview',
          data: {
            title: 'Test report 1',
            blocks: [
              {
                id: 'insight-1',
                type: 'insight_card',
                version: 'v1',
                config: {
                  heading: 'CTR is **declining** week over week',
                  heading_badge: 'Needs Attention',
                  border_color: 'yellow' as const,
                  layout: {
                    left: ['chart-ctr'],
                    right: ['text-ctr', 'metrics-ctr'],
                  },
                },
                block_elements: [
                  {
                    id: 'chart-ctr',
                    type: 'line_chart' as const,
                    version: 'v1',
                    content: {
                      title: 'CTR Trend (Last 4 Weeks)',
                      data: [
                        { week: 'Week 1', ctr: 3.8 },
                        { week: 'Week 2', ctr: 3.5 },
                        { week: 'Week 3', ctr: 3.1 },
                        { week: 'Week 4', ctr: 2.7 },
                      ],
                      xAxisKey: 'week',
                      config: {
                        ctr: { label: 'CTR %', color: '#eab308' },
                      },
                      showLegend: false,
                    },
                  },
                  {
                    id: 'text-ctr',
                    type: 'text' as const,
                    version: 'v1',
                    content:
                      'Click-through rate has dropped **29%** over the past 4 weeks. Consider refreshing ad creatives.',
                  },
                  {
                    id: 'metrics-ctr',
                    type: 'metrics_grid' as const,
                    version: 'v1',
                    content: {
                      header: 'Key Metrics',
                      cells: [
                        {
                          value: '2.7%',
                          change: '-29%',
                          label: 'Current CTR',
                          changeColor: 'text-yellow-600',
                        },
                        {
                          value: '3.8%',
                          label: 'Baseline CTR',
                        },
                      ],
                      columns: 2 as const,
                    },
                  },
                ],
              },
              {
                id: 'insight-2',
                type: 'insight_card',
                version: 'v1',
                config: {
                  heading: 'Conversions are **up** significantly',
                  heading_badge: 'Good',
                  border_color: 'green' as const,
                },
                block_elements: [
                  {
                    id: 'text-conversions',
                    type: 'text' as const,
                    version: 'v1',
                    content:
                      'Despite lower CTR, conversion rate has improved by **18%**, suggesting better audience targeting.',
                  },
                  {
                    id: 'metrics-conversions',
                    type: 'metrics_grid' as const,
                    version: 'v1',
                    content: {
                      cells: [
                        {
                          value: '892',
                          change: '+18%',
                          label: 'Conversions',
                          changeColor: 'text-green-600',
                        },
                        {
                          value: '$12.40',
                          change: '-22%',
                          label: 'Cost per Conversion',
                          changeColor: 'text-green-600',
                        },
                      ],
                      columns: 2 as const,
                    },
                  },
                ],
              },
            ] as unknown as UIBlock[],
          },
        },
      },
    ],
  },
  {
    type: 'metrics_grid',
    version: 'v1',
    content: {
      header: 'Overall Campaign Summary',
      cells: [
        { value: '$45.2k', change: '+8%', label: 'Total Spend', changeColor: 'text-green-600' },
        { value: '12,340', change: '+15%', label: 'Clicks', changeColor: 'text-green-600' },
        { value: '892', change: '+18%', label: 'Conversions', changeColor: 'text-green-600' },
      ],
      columns: 3,
    },
  },
  {
    type: 'inline_suggestions',
    version: 'v1',
    content: {
      label: 'What would you like to do next?',
      items: [
        { label: 'Refresh ad creatives', value: 'refresh_creatives' },
        { label: 'Deep dive into CTR', value: 'ctr_deep_dive' },
        { label: 'View full report', value: 'full_report' },
      ],
    },
  },
];


/** Blocks for the second assistant message — triggers a report_preview slot */
const reportPreviewBlocks: UIBlock[] = [
  {
    type: 'text',
    version: 'v1',
    content: 'Here is the EU West campaign preview from the latest report.',
    behaviours: [
      {
        id: 'b-report-preview-1',
        trigger: 'auto',
        action: {
          id: 'a-report-preview-1',
          type: 'agent_experience',
          agent_short_name: 'insight',
          experience: 'report_preview',
          data: {
            title: 'EU West Q4 Preview',
            insight_id: 'mock-insight-1',
            page_group_id: 'pg-campaigns',
            page_id: 'page-eu-west',
          },
        },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// content_editor mock data
// ---------------------------------------------------------------------------

const MOCK_LINKEDIN_POST_MARKDOWN = `Hotels spend ~3-5% of revenue on technology.

Banks spend 7-10%.
Airlines spend 5-7%.

And yet hotels handle more real-time operational complexity per square foot than almost any other business.

Housekeeping. F&B. Revenue management. Guest preferences. Maintenance. Distribution across 15+ channels. All happening simultaneously.

The hotel industry isn't behind on tech.
It's underfunded on tech.

The next wave of hospitality won't be won by the brands with the most properties.
It'll be won by the ones who invest in the right systems — early.`;

const MOCK_RESEARCH_RESPONSE_MARKDOWN = `Here's a synthesis of the most important trending topics in the hotel industry right now, based on current research:

### 1. AI Reshaping Distribution & Discovery (The Biggest Story)

This is arguably the single most disruptive trend. The number of consumers searching via LLMs for travel products has ballooned in the last 12 months, and hotels that aren't discoverable in LLM search results risk being invisible to consumers.

The shift is threefold:

- **From SEO to AIO (AI Optimization)** — Unlike traditional SEO, AI search optimization requires hotels to structure information in ways AI systems can process and surface, demanding investment in digital infrastructure and marketing technology.
- **Agentic Commerce** — The shift toward agentic AI is being called the most significant trend since the move from offline to online booking two decades ago, with analysts expecting over 54% of travelers to use AI agents for trip planning by 2026.
- **MCP (Model Context Protocol) as the new battleground** — Standardized interfaces enabling AI agents to access hotel data programmatically will become the new distribution battleground, with CRS systems positioned to become dominant because they hold the canonical source of truth for availability, rates, and inventory.

### 2. Profitability Compression — The Margin Crisis

This is the most urgent operational story. Demand stabilized, but expense growth — particularly labor, insurance, and fixed operating standards — continued to outpace revenue gains, compressing gross operating profit across nearly all segments.

The numbers are stark:

- Hotel labor costs have increased **15.3%** from 2019 to 2025, outpacing the 12.8% growth in total operating revenue. Insurance is up more than **100%**, utilities nearly 30%, and property maintenance nearly 25%.
- RevPAR growth has softened and now trails inflation in most markets. The question shaping 2026 budgets is "Is our labor model right-sized?" — not "How much can we grow revenue?"
- The first 2026 forecast projects full-year RevPAR growth of just **0.6%**, following 2025's 0.3% decline — the first non-recessionary RevPAR decline ever recorded.

### 3. The K-Shaped Economy & Luxury Bifurcation

The hotel industry is heading into 2026 with a widening gap between luxury and the rest of the market, as RevPAR growth at the high end far outpaces other segments. This bifurcation is deepening — even within luxury itself, a rift is growing between the ultrawealthy segment and the broader luxury market.

Meanwhile, economy and midscale segments are struggling, creating a two-track industry where different playbooks are needed depending on where you sit on the chain scale.

### 4. Brand Proliferation & Conversion Mania

The industry is at or past the point of brand saturation, with public companies under tremendous pressure for net unit growth, forcing them to create micro brands and subsets of brands to expand their footprint. Hilton, Marriott, and Wyndham all launched new brands recently, and conversions (rather than new builds) are the preferred growth vehicle in a high-construction-cost environment.

### 5. Agentic AI in Operations

Beyond distribution, hotels are moving from experimenting with AI to embedding agents directly into daily operations — configuring rooms, preparing arrivals, handling routine guest requests, and monitoring anomalies. Key developments:

- AI has reduced call abandonment rates by **6-8%**, increased reservation conversion by **25-35%**, and decreased call volume by **20-30%**.
- Revenue management and commercial strategy are becoming autonomous, with AI systems not just suggesting pricing tweaks but actually making commercial decisions.`;

const MOCK_LINKEDIN_POST_REVISED_MARKDOWN = `Hotels spend ~3-5% of revenue on technology.

Banks spend 7-10%.
Airlines spend 5-7%.

And yet hotels handle more real-time operational complexity per square foot than almost any other business.

Housekeeping. F&B. Revenue management. Guest preferences. Maintenance. Distribution across 15+ channels. All happening simultaneously.

The hotel industry isn't behind on tech.
It's underfunded on tech.

And now AI is widening that gap faster than ever.

Hotels still stitching together spreadsheets are competing against properties where pricing, staffing, and guest personalization run on autopilot.

The next wave of hospitality won't be won by the brands with the most properties.
It'll be won by the ones who invest in the right AI systems — early.

Where is your hotel on that spectrum?`;

const contentPreviewBlocks: UIBlock[] = [
  {
    type: 'text',
    version: 'v1',
    content:
      "Here's your LinkedIn post draft targeting hotel marketing and revenue teams on AI reshaping distribution & discovery. You can edit it below and save when you're happy with it.",
    behaviours: [
      {
        id: 'b-content-preview-1',
        trigger: 'auto',
        action: {
          id: 'a-content-preview-1',
          type: 'agent_experience',
          agent_short_name: 'linkedin-post-generator',
          experience: 'content_editor',
          data: {
            title: 'LinkedIn Post Draft',
            content: MOCK_LINKEDIN_POST_MARKDOWN,
          },
        },
      },
    ],
  },
];

const MOCK_CONTENT_PREVIEW_MESSAGES: ChatEntry[] = [
  // Step 1: User asks for research
  {
    id: 'mock-user-cp-1',
    kind: 'user',
    content:
      'What are the most important topics in Hotel Industry that are trending currently? Do some research and come back',
  },
  // Step 2: Agent says it will research
  {
    id: 'mock-assistant-cp-1',
    kind: 'assistant-progressive',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content:
          'Let me research the current trending topics in the hotel industry.',
      },
    ],
    isComplete: true,
  },
  // Step 3: Tool call — web search (auto-approved)
  {
    id: 'mock-tool-web-search',
    kind: 'tool-approval',
    toolCalls: [
      {
        id: 'tc-web-search-1',
        name: 'web_search',
        description: 'Searched the web',
        requires_approval: false,
      },
    ],
    resolved: true,
    resolution: 'approved' as const,
  },
  // Step 4: Agent responds with research synthesis
  {
    id: 'mock-assistant-cp-2',
    kind: 'assistant-progressive',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: MOCK_RESEARCH_RESPONSE_MARKDOWN,
      },
    ],
    isComplete: true,
  },
  // Step 5: User picks a topic
  {
    id: 'mock-user-cp-2',
    kind: 'user',
    content: "okay let's write about AI reshaping distribution and discovery",
  },
  // Step 6: Agent asks format
  {
    id: 'mock-assistant-cp-3',
    kind: 'assistant-progressive',
    blocks: [
      {
        type: 'radio',
        version: 'v1',
        content: {
          label: 'What format are you thinking?',
          options: [
            { value: 'blog_post', label: 'Blog post/article' },
            { value: 'linkedin_post', label: 'LinkedIn Post' },
            { value: 'internal_doc', label: 'Internal doc/brief' },
          ],
          selectedValue: 'linkedin_post',
        },
      },
    ],
    isComplete: true,
  },
  // Step 7: User selects LinkedIn Post
  {
    id: 'mock-user-cp-3',
    kind: 'user',
    content: 'LinkedIn Post',
  },
  // Step 8: Agent asks target audience
  {
    id: 'mock-assistant-cp-4',
    kind: 'assistant-progressive',
    blocks: [
      {
        type: 'radio',
        version: 'v1',
        content: {
          label: "Who's the target audience?",
          options: [
            { value: 'hotel_owners', label: 'Hotel owners & operators' },
            {
              value: 'hotel_marketing',
              label: 'Hotel marketing / revenue teams',
            },
            { value: 'hotel_tech', label: 'Hotel tech vendors' },
            {
              value: 'general_hospitality',
              label: 'General hospitality industry',
            },
          ],
          selectedValue: 'hotel_marketing',
        },
      },
    ],
    isComplete: true,
  },
  // Step 9: User selects audience
  {
    id: 'mock-user-cp-4',
    kind: 'user',
    content: 'Hotel marketing / revenue teams',
  },
  // Step 10: Agent delivers the LinkedIn post with content_editor slot
  {
    id: 'mock-assistant-cp-5',
    kind: 'assistant-progressive',
    blocks: contentPreviewBlocks,
    isComplete: true,
    chat_history_id: 'mock-chat-history-content-preview',
  },
  // Step 11: User asks for revision
  {
    id: 'mock-user-cp-5',
    kind: 'user',
    content: 'Can you be bit more specific and a section on AI?',
  },
  // Step 12: Agent delivers revised LinkedIn post with AI section
  {
    id: 'mock-assistant-cp-6',
    kind: 'assistant-progressive',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content:
          "I've revised the post to be more specific and added a section on AI's impact. Take a look below.",
        behaviours: [
          {
            id: 'b-content-preview-2',
            trigger: 'auto',
            action: {
              id: 'a-content-preview-2',
              type: 'agent_experience',
              agent_short_name: 'linkedin-post-generator',
              experience: 'content_editor',
              data: {
                title: 'LinkedIn Post Draft (Revised)',
                content: MOCK_LINKEDIN_POST_REVISED_MARKDOWN,
              },
            },
          },
        ],
      },
    ],
    isComplete: true,
    chat_history_id: 'mock-chat-history-content-preview-revised',
  },
];

// ---------------------------------------------------------------------------
// keyword_review mock data
// ---------------------------------------------------------------------------

/**
 * When true, the keyword review slot will receive mock ApprovalItem[] data
 * so the panel renders a populated table instead of hitting the real approvals API.
 *
 * Auto-derived: enabled when mock behaviour is on AND scenario is keyword_review.
 * Set to `true` manually to force mock data even without mock behaviour mode.
 */
export const ENABLE_MOCK_KEYWORD_DATA = ENABLE_MOCK_BEHAVIOUR && ACTIVE_MOCK_SCENARIO === 'keyword_review';

const MOCK_APPROVAL_ID = 'mock-approval-001';
const MOCK_TENANT_ID = 'mock-tenant-001';
const now = new Date().toISOString();

/**
 * 18 mock approval items totalling $3,400 across all four categories.
 * Budget Drainers (6): $1,800 | Low Intent (5): $850
 * Window Shopping (4): $520  | Nascent Spenders (3): $230
 */
const item = (
  id: string,
  search_term: string, keyword: string, match_type: string,
  cost: number, score: number, category_filter: string, justification: string,
  conv_rate: number, cost_per_conv: number, impr: number, interaction_rate: number, campaign: string,
): ApprovalItem => ({
  id, approval_id: MOCK_APPROVAL_ID, tenant_id: MOCK_TENANT_ID,
  status: null, created_at: now, updated_at: now,
  data: { search_term, keyword, match_type, cost, score, category_filter, justification, conv_rate, cost_per_conv, impr, interaction_rate, campaign },
});

export const MOCK_APPROVAL_ITEMS: ApprovalItem[] = [
  // ── Budget Drainers (6 items = $1,800) ──────────────────────────────────
  item('1', 'free crm software download', 'crm software', 'BROAD', 425.00, 95, 'potential budget drainer',
    'High spend with 0% conversion rate. "Free" intent unlikely to convert.', 0, 0, 5200, 3.2, 'Brand - CRM Solutions'),
  item('2', 'crm jobs hiring near me', 'crm', 'BROAD', 342.50, 92, 'potential budget drainer',
    'Job-seeking intent, not relevant to software purchase.', 0, 0, 3150, 4.1, 'Generic - CRM'),
  item('3', 'what is crm wikipedia', 'what is crm', 'PHRASE', 318.70, 90, 'potential budget drainer',
    'Informational/Wikipedia query — zero purchase intent.', 0, 0, 6800, 1.8, 'Generic - CRM'),
  item('4', 'crm internship summer 2025', 'crm internship', 'BROAD', 289.60, 88, 'potential budget drainer',
    'Internship-seeking intent. Irrelevant to SaaS purchase.', 0, 0, 2400, 3.5, 'Generic - CRM'),
  item('5', 'free crm template excel', 'crm template', 'BROAD', 256.80, 86, 'potential budget drainer',
    'Looking for free Excel template, not a paid CRM product.', 0, 0, 4100, 2.9, 'Brand - CRM Solutions'),
  item('6', 'crm open source github', 'crm open source', 'PHRASE', 167.40, 82, 'potential budget drainer',
    'Open-source seeker — unlikely to convert on paid plans.', 0, 0, 1900, 3.8, 'Generic - CRM'),
  // ── Low Intent (5 items = $850) ─────────────────────────────────────────
  item('7', 'salesforce vs hubspot reddit', 'salesforce vs', 'PHRASE', 234.50, 74, 'potential low intent',
    'Reddit-seeking query — user wants community opinions, not ads.', 0, 0, 8900, 0.6, 'Competitor - Salesforce'),
  item('8', 'crm software reviews 2025', 'crm reviews', 'BROAD', 186.30, 72, 'potential low intent',
    'Review-stage research — high impressions but minimal engagement.', 0, 0, 12400, 0.4, 'Generic - CRM'),
  item('9', 'hubspot crm tutorial youtube', 'hubspot tutorial', 'PHRASE', 168.40, 70, 'potential low intent',
    'Tutorial/YouTube intent — learning, not buying.', 0, 0, 7200, 0.8, 'Competitor - HubSpot'),
  item('10', 'crm best practices blog', 'crm best practices', 'BROAD', 142.80, 68, 'potential low intent',
    'Educational content seeker. Very low interaction rate.', 0, 0, 9600, 0.5, 'Generic - CRM'),
  item('11', 'zoho crm login page', 'zoho crm login', 'EXACT', 118.00, 65, 'potential low intent',
    'Navigational query — user trying to log in to competitor product.', 0, 0, 5400, 0.3, 'Competitor - Zoho'),
  // ── Window Shopping (4 items = $520) ────────────────────────────────────
  item('12', 'cheapest crm for small business', 'cheapest crm', 'BROAD', 178.90, 72, 'potential window shopping',
    'Price-sensitive query — high clicks but near-zero conversion.', 0.3, 596.33, 3200, 5.8, 'Generic - CRM Startups'),
  item('13', 'crm software pricing comparison', 'crm pricing', 'PHRASE', 145.90, 70, 'potential window shopping',
    'Comparison shopping — clicks ads but does not convert.', 0.5, 145.90, 2800, 5.1, 'Generic - CRM'),
  item('14', 'crm free trial no credit card', 'crm free trial', 'BROAD', 112.60, 66, 'potential window shopping',
    'Free trial seeker avoiding commitment — low conversion likelihood.', 0.4, 281.50, 2100, 6.2, 'Brand - CRM Solutions'),
  item('15', 'crm under 10 dollars month', 'cheap crm', 'EXACT', 82.60, 62, 'potential window shopping',
    'Ultra-budget query — price point far below our plans.', 0.2, 413.00, 1800, 4.6, 'Generic - CRM Startups'),
  // ── Nascent Spenders (3 items = $230) ───────────────────────────────────
  item('16', 'crm certification online course', 'crm certification', 'PHRASE', 98.40, 60, 'potential nascent spender',
    'Certification/training intent, not buyer. Low impressions but leaking spend.', 0, 0, 320, 2.2, 'Generic - CRM'),
  item('17', 'crm consultant freelance rates', 'crm consultant', 'BROAD', 72.50, 55, 'potential nascent spender',
    'Consulting-rate research — not a product buyer.', 0, 0, 180, 1.8, 'Generic - CRM'),
  item('18', 'crm database schema example', 'crm database', 'PHRASE', 59.10, 50, 'potential nascent spender',
    'Developer/technical query — building own CRM, not buying.', 0, 0, 140, 1.4, 'Generic - CRM'),
];

/**
 * Blocks for the keyword review slot — auto-triggered keyword_review experience.
 * Used for the initial "all keywords" view (no category filter).
 */
const keywordReviewAutoBlocks: UIBlock[] = [
  {
    type: 'text',
    version: 'v1',
    content: '',
    behaviours: [
      {
        id: 'b-keyword-review-auto-1',
        trigger: 'auto',
        action: {
          id: 'a-keyword-review-auto-1',
          type: 'agent_experience',
          agent_short_name: 'mardi',
          experience: 'keyword_review',
          data: {},
        },
      },
    ],
  },
];

/**
 * Blocks for the filtered keyword review — auto-triggered with category filter.
 * Shows only "potential budget drainer" keywords.
 */
const keywordReviewFilteredBlocks: UIBlock[] = [
  {
    type: 'text',
    version: 'v1',
    content:
      'Here are the keywords classified as budget drainers — high spend with zero or near-zero conversion rates.',
    behaviours: [
      {
        id: 'b-keyword-review-filtered-1',
        trigger: 'auto',
        action: {
          id: 'a-keyword-review-filtered-1',
          type: 'agent_experience',
          agent_short_name: 'mardi',
          experience: 'keyword_review',
          data: {
            category: 'potential budget drainer',
          },
        },
      },
    ],
  },
];

/**
 * Mock keyword review conversation flow:
 *
 * 1. User message simulates the card click → pendingBlocks flow:
 *    text + blocks with auto-triggered keyword_review (all keywords)
 *
 * 2. User asks for filtered view (budget drainers)
 *
 * 3. Assistant responds with auto-triggered keyword_review filtered to budget drainers
 */
const MOCK_KEYWORD_REVIEW_MESSAGES: ChatEntry[] = [
  // Step 1: Simulates the card click → pending message with keyword_review slot
  {
    id: 'mock-user-kr-1',
    kind: 'user',
    content: 'Review 18 negative keywords wasting $3,400',
    blocks: keywordReviewAutoBlocks,
  },
  // Step 2: User asks for filtered view
  {
    id: 'mock-user-kr-2',
    kind: 'user',
    content: 'Show me just keywords that are budget drainers',
  },
  // Step 3: Agent responds with filtered keyword review
  {
    id: 'mock-assistant-kr-3',
    kind: 'assistant-progressive',
    blocks: keywordReviewFilteredBlocks,
    isComplete: true,
    chat_history_id: 'mock-chat-history-keyword-review-filtered',
  },
];

// ---------------------------------------------------------------------------
// insight_preview mock messages (original)
// ---------------------------------------------------------------------------

const MOCK_INSIGHT_PREVIEW_MESSAGES: ChatEntry[] = [
  {
    id: 'mock-user-1',
    kind: 'user',
    content: 'Analyse my Google Ads campaign performance for Q4',
  },
  {
    id: 'mock-assistant-1',
    kind: 'assistant-progressive',
    blocks: blocksWithBehaviour,
    isComplete: true,
  },
  {
    id: 'mock-user-2',
    kind: 'user',
    content: 'Show me the EU West preview from the report',
  },
  {
    id: 'mock-assistant-2',
    kind: 'assistant-progressive',
    blocks: reportPreviewBlocks,
    isComplete: true,
  },
];

/**
 * Mock chat messages that simulate a conversation with behaviour slots.
 * Uses ACTIVE_MOCK_SCENARIO to decide which set of messages to export.
 */
export const MOCK_BEHAVIOUR_CHAT_MESSAGES: ChatEntry[] =
  ACTIVE_MOCK_SCENARIO === 'keyword_review'
    ? MOCK_KEYWORD_REVIEW_MESSAGES
    : ACTIVE_MOCK_SCENARIO === 'content_editor'
      ? MOCK_CONTENT_PREVIEW_MESSAGES
      : MOCK_INSIGHT_PREVIEW_MESSAGES;
